import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { DemoApiError } from '../api/client';
import { useSession } from '../session/SessionContext';
import { brand } from '../theme';

function isValidEmail(value: string): boolean {
  // Intentionally light — any syntactically plausible email is fine for the demo.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function SignInPage() {
  const { session, signIn } = useSession();
  const [passcode, setPasscode] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (session) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setEmailError(null);

    if (!isValidEmail(email)) {
      setEmailError('Enter a valid email address');
      return;
    }
    if (!passcode.trim()) {
      setError('Enter the demo passcode');
      return;
    }

    setSubmitting(true);
    try {
      await signIn(passcode.trim(), email);
    } catch (err) {
      if (err instanceof DemoApiError && err.code === 'INVALID_PASSCODE') {
        setError('Incorrect passcode. Check with the demo owner and try again.');
      } else if (err instanceof TypeError || (err instanceof DemoApiError && err.status === 0)) {
        setError('Network error. Check your connection and retry.');
      } else if (err instanceof Error) {
        setError(err.message || 'Something went wrong. Retry.');
      } else {
        setError('Something went wrong. Retry.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: brand.canvas,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2.5,
        py: 6,
      }}
    >
      <Stack
        component="form"
        onSubmit={onSubmit}
        spacing={0}
        sx={{ width: '100%', maxWidth: 400 }}
      >
        <Stack alignItems="center" spacing={2.25} sx={{ mb: 4.75 }}>
          <Box
            component="img"
            src="/logo-single.svg"
            alt="Cheddar"
            sx={{ width: 132, height: 132, display: 'block' }}
          />
          <Typography
            component="h1"
            sx={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.045em', lineHeight: 1 }}
          >
            Cheddar
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              color: brand.muted,
              textAlign: 'center',
              lineHeight: 1.5,
              textWrap: 'pretty',
            }}
          >
            End-client wallet demo.
            <br />
            Sign in with your email for this session.
          </Typography>
        </Stack>

        <Stack spacing={1.75}>
          {error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setError(null)}>
                  Dismiss
                </Button>
              }
            >
              {error}
            </Alert>
          ) : null}

          <TextField
            label="Demo passcode"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            autoComplete="off"
            required
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(emailError)}
            helperText={emailError ?? 'Used as the demo customer id'}
            autoComplete="email"
            required
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ mt: 0.75 }}
          >
            {submitting ? 'Signing in…' : 'Continue'}
          </Button>
        </Stack>

        <Box
          sx={{
            mt: 3.25,
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-start',
            p: 1.75,
            borderRadius: '12px',
            backgroundColor: brand.goldWash,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              flex: 'none',
              mt: '7px',
              borderRadius: '50%',
              backgroundColor: brand.amber,
            }}
          />
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: brand.infoFg }}>
            Sales demo environment. Test funds only — no real customer assets are held here.
            The email becomes the end-client identity for this session (no verification).
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
